import React, { useState, useEffect, useMemo, FC } from 'react';
import {
  BoxV2 as Box,
  Flex,
  Table,
  TableFilterV2 as TableFilter,
  ButtonV2,
  useTable,
  useFilters,
  FilterType,
  ExpandableText,
  Drawer,
} from 'portal-commons';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';

import PageContainer from '@/components/PageContainer';
import { PageTitle } from '@/components';
import BrandDetailDE from '@/applications/MnoPortal/Brands/containers/BrandDetail/BrandDetailDE';
import { eventController, enumController } from '@/apis/core';
import {
  getBrandNameAutocompleteOptions,
  getCspNameAutocompleteOptions,
} from '@/apis/services';
import { Loader } from '@/shared_elements';
import { MAX_EVENT_RECORDS, getDateRangePresets } from '../constants';
import { EventData, EventType } from '../types';
import { convertTimeWithTimezone } from '@/utils/time';
import { getEventTypeImage } from '@/constants';
import { toastFlashMessage } from '@/utils';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import { generateAndDownloadFile } from '@/utils/file';
import { useDispatch } from 'react-redux';

const FILTER_CONFIG = {
  brandUid: {
    type: FilterType.Search,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Search,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Search,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  eventType: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Event Type',
  },
  dateRange: {
    type: FilterType.DateRange,
    label: 'Date Range',
    placeholder: 'Select Date Range',
    dateRangePresets: getDateRangePresets(),
    // workaround for DateRangePicker bug
    delayUpdate: false,
  },
};

const TABLE_HEAD_ROWS = [
  { label: 'EVENT', id: 'eventType', sortable: true },
  { label: 'CATEGORY', id: 'categoryType', sortable: true },
  { label: 'BRAND ID', id: 'brandUid', sortable: true },
  { label: 'CSP', id: 'cspName', sortable: false },
  { label: 'DATE & TIME', id: 'createDate', sortable: true },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) =>
        getBrandNameAutocompleteOptions('displayName', value),
    },
    cspName: {
      minLength: 2,
      load: (value: string) =>
        getCspNameAutocompleteOptions('displayName', value),
    },
  },
};

const EventsDE: FC = () => {
  const dispatch = useDispatch();
  const [viewingBrand, setViewingBrand] = useState<string | null>(null);

  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const {
    appliedFilters,
    configs: filterConfigs,
    additionalFilters,
    candidateFilters,
    handleEdit: handleCandidateFiltersChange,
    handleApply: handleAppliedFiltersChange,
    setOptions,
  } = filter;

  const dateRangePreprocess = (queries: Record<string, unknown>) => {
    const { dateRange, ...restQueries } = queries;
    const DATE_FORMAT = 'YYYY-MM-DD';
    if (dateRange) {
      const dates = (dateRange as string).replace(/\s/gi, '').split('-');
      if (dates.length === 2) {
        return {
          ...restQueries,
          startDate: convertTimeWithTimezone(dates[0], undefined, DATE_FORMAT),
          endDate: convertTimeWithTimezone(dates[1], undefined, DATE_FORMAT),
        };
      }
    }
    return restQueries;
  };

  const getQuery = (tableFilters: Record<string, unknown> = {}) => {
    return dateRangePreprocess({
      ...tableFilters,
      ...filter.appliedFilters,
      ...filter.additionalFilters,
    });
  };

  const table = useTable<EventData>(
    async (tableFilters) => {
      const query = getQuery(tableFilters);
      const response = await eventController.getEventHistory(query);
      return {
        data:
          response?.records?.map((r: EventData) => ({
            ...r,
            createDate: convertTimeWithTimezone(r.createDate),
          })) || [],
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 10,
        total: response?.totalRecords || 0,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const fetchFilterOptions = async () => {
    const eventTypes = await enumController.getEventTypes();
    setOptions(
      'eventType',
      eventTypes.map((item: EventType) => ({
        label: item.id,
        value: item.id,
      }))
    );
  };

  const setLoadingMessage = (message: string) => {
    dispatch({
      type: SET_LOADING_MESSAGE,
      payload: message,
    });
  };

  const clearLoadingMessage = () => {
    dispatch({
      type: CLEAR_LOADING_MESSAGE,
    });
  };

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    // the download query should be equal to the last successful list query
    const { page, ...restQuery } = getQuery(table.filter);
    const response = await eventController.downloadEventHistoryAsCsv(restQuery);
    if (response) {
      const file = new File([response], 'event-history.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    clearLoadingMessage();
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const tableData = useMemo(() => {
    return table.tableData.map((event) => ({
      id: event.id,
      eventType: (
        <Flex sx={{ alignItems: 'center' }} data-testid="eventType">
          <Box sx={{ mr: 's' }}>{getEventTypeImage(event.eventType)}</Box>
          <span>{event.eventType || '--'}</span>
        </Flex>
      ),
      categoryType: (
        <span data-testid="categoryType">{event.categoryType || '--'}</span>
      ),
      brandUid: (
        <Box
          as="span"
          sx={{
            ...(event.brandUid && {
              '&:hover': {
                cursor: 'pointer',
                fontWeight: 'bold',
              },
            }),
          }}
          data-testid="brandUid"
          onClick={(e) => {
            e.stopPropagation();
            if (event.brandUid) {
              setViewingBrand(event.brandUid);
            }
          }}
        >
          {event.brandUid || '--'}
        </Box>
      ),
      cspName: <span data-testid="cspName">{event.cspName || '--'}</span>,
      createDate: (
        <div data-testid="createDate" style={{ minWidth: 140 }}>
          {event.createDate || '--'}
        </div>
      ),
      description: (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 1fr)',
            width: '100%',
          }}
          data-testid="description"
        >
          <ExpandableText text={event.description} maxWidth="100%" />
        </Box>
      ),
    }));
  }, [table.tableData]);

  if (table.pageLoading) return <Loader />;

  return (
    <PageContainer
      sx={{
        paddingX: '96px',
        rowGap: null,
        '& [data-testid="eventsTable"]': {
          'td:has([data-testid="description"])': {
            width: '100%',
          },
        },
      }}
      data-testid="events-de"
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          marginTop: '26px',
          marginBottom: '26px',
        }}
      >
        <PageTitle
          note={
            <span>
              This page can only show the latest{' '}
              {MAX_EVENT_RECORDS.toLocaleString('en-US')} records. Contact
              support for details on your remaining records.
            </span>
          }
        >
          {`${table.total} Events`}
        </PageTitle>
        <ButtonV2
          variant="outline"
          shape="square"
          size="lg"
          startAdornment={faArrowDownToLine}
          onClick={handleDownload}
          disabled={!table.total}
          data-testid="downloadEventsButton"
        >
          Download
        </ButtonV2>
      </Flex>

      <Box sx={{ mb: 'm', width: '100%' }}>
        <TableFilter
          portal="mno"
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleCandidateFiltersChange}
          onAppliedValuesChange={handleAppliedFiltersChange}
          data-testid="eventsTableFilters"
        />
      </Box>

      <Table
        loading={table.loading}
        headRows={TABLE_HEAD_ROWS}
        emptyState="no events to view"
        data={tableData}
        rowKey="id"
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        filter={table.filter}
        pagination={table.pagination}
        emptyBlockHeight="180px"
        data-testid="eventsTable"
      />

      <Drawer
        open={viewingBrand !== null}
        onClose={() => setViewingBrand(null)}
        width="70%"
      >
        <BrandDetailDE brandUid={viewingBrand} />
      </Drawer>
    </PageContainer>
  );
};

export default EventsDE;
